/**
 * Settings ▸ Repository roles — the five documentation capabilities
 * (guided-repo-setup Phase B, #571 lineage).
 *
 * Each capability is tri-state: unset (no decision — the product may nudge),
 * enabled (a repo assigned; components spans MANY repos), or disabled (a
 * deliberate "we don't use this" the product stops asking about, always
 * re-enableable here). One repository can play every role — that is how the
 * free plan starts — so nothing on this page claims dedicated repositories
 * are required.
 *
 * The fitness role doubles as the CI publish target: saving it also updates
 * the guardrails CI settings (resolving the GitHub App installation), so the
 * Checks pipeline and the capability model cannot drift apart. The CI wiring
 * details themselves stay on CiIntegrationTab below, picker-less.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  CheckCircle, AlertCircle, FolderGit2, FileText, PenTool,
  ClipboardList, HeartPulse, Boxes,
} from 'lucide-react';
import { Spinner, LoadingState } from '@systemdox/ui';
import {
  specsApi, CAPABILITY_KEYS, type Capabilities, type CapabilityKey,
} from '@/services/specsApi';
import { guardrailsApi } from '@/services/guardrailsApi';
import { githubApi, type Installation, type PlanUsage, type Repo } from '@/services/githubApi';
import { cn } from '@/lib/utils';
import CreateRepoInline from './CreateRepoInline';

const ROWS: {
  key: CapabilityKey;
  icon: typeof FileText;
  title: string;
  /** Canonical path contract from the Documentation Standard, shown as a chip. */
  path: string;
  description: string;
  /** Prefill for the inline create-repo form; single-repo rows only —
   *  components picks from already-connected repos, nothing to create. */
  suggestedName?: string;
}[] = [
  {
    key: 'requirements',
    icon: FileText,
    title: 'Requirements',
    path: 'docs/requirements/',
    suggestedName: 'docs-requirements',
    description: 'Backs the Requirements pillar — requirement documents publish to docs/requirements/ in this repo, one folder per document with its living body under specs/.',
  },
  {
    key: 'design',
    icon: PenTool,
    title: 'Design',
    path: 'docs/design/',
    suggestedName: 'docs-design',
    description: 'Backs the Design pillar — design documents publish to docs/design/ in this repo; ADRs live beside them at docs/design/adr/ as ADR-NNN-title.md.',
  },
  {
    key: 'specs',
    icon: ClipboardList,
    title: 'Specs',
    path: 'specs/changes/',
    suggestedName: 'docs-specs',
    description: 'Spec-driven changes — one folder per change under specs/changes/ (proposal, requirement deltas, tasks); completed changes fold into the requirements and retire to specs/archive/.',
  },
  {
    key: 'fitness',
    icon: HeartPulse,
    title: 'Fitness tests',
    path: 'scripts/check_*.sh',
    suggestedName: 'fitness-tests',
    description: 'Architecture checks (Checks) — "Publish to CI" opens PRs against this repo that install scripts/check_*.sh and run_all.sh, run by its CI on every pull request.',
  },
  {
    key: 'components',
    icon: Boxes,
    title: 'Component docs',
    path: 'docs/ in each repo',
    description: 'Low-level design living in the docs/ folder of each system-component repository — architecture notes, runbooks, and an api.md linking the service contract.',
  },
];

/** Per-row editor state. active+repo → enabled; active+'' → unset;
 *  disabled → the explicit opt-out. */
interface RowState {
  active: boolean;
  repo: string;
  repos: string[];
}

function rowsFromCapabilities(caps: Capabilities | undefined, ciRepo: string): Record<CapabilityKey, RowState> {
  const out = {} as Record<CapabilityKey, RowState>;
  for (const key of CAPABILITY_KEYS) {
    const cap = caps?.[key];
    out[key] = {
      active: cap?.status !== 'disabled',
      // A pre-capability tenant with a CI target already chose its fitness
      // repo once — seed the selector so saving here doesn't ask again.
      repo: cap?.status === 'enabled' ? (cap.repo ?? '') : key === 'fitness' ? ciRepo : '',
      repos: cap?.status === 'enabled' ? (cap.repos ?? []) : [],
    };
  }
  return out;
}

export default function DocReposTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [repos, setRepos] = useState<Repo[]>([]);
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [planUsage, setPlanUsage] = useState<PlanUsage | null>(null);
  const [rows, setRows] = useState<Record<CapabilityKey, RowState> | null>(null);
  const [ciRepo, setCiRepo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      specsApi.getDocRepos(),
      githubApi.listRepos(),
      githubApi.listInstallations().catch(() => [] as Installation[]),
      githubApi.getPlanUsage().catch(() => null),
      guardrailsApi.getCiSettings().catch(() => null),
    ])
      .then(([docRepos, repoList, installs, usage, ci]) => {
        const configuredCi = ci?.configured && ci.owner && ci.repo ? `${ci.owner}/${ci.repo}` : '';
        setRows(rowsFromCapabilities(docRepos.capabilities, configuredCi));
        setRepos(repoList);
        setInstallations(installs);
        setPlanUsage(usage);
        setCiRepo(configuredCi);
      })
      .catch(() => setError('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const connectedRepos = useMemo(() => repos.filter((r) => r.connected), [repos]);

  /** Repos declaring themselves host for each pillar in their committed
   *  .github/docs-standard.yml (Documentation Standard §5.7) — the durable
   *  record the selectors here should agree with. */
  const declaredHosts = useMemo(() => {
    const out = {} as Record<string, string[]>;
    for (const r of repos) {
      const pillars = r.docs_standard?.pillars;
      if (!pillars) continue;
      for (const [key, role] of Object.entries(pillars)) {
        if (role === 'host') (out[key] ??= []).push(r.full_name);
      }
    }
    return out;
  }, [repos]);

  function setRow(key: CapabilityKey, patch: Partial<RowState>) {
    setRows((prev) => (prev ? { ...prev, [key]: { ...prev[key], ...patch } } : prev));
  }

  /** App-created repos join the installation automatically — append to the
   *  picker set and select for the row, no refetch needed. */
  function handleRepoCreated(key: CapabilityKey, repo: Repo) {
    setRepos((prev) =>
      prev.some((r) => r.full_name === repo.full_name) ? prev : [...prev, repo],
    );
    setRow(key, { repo: repo.full_name });
  }

  async function refreshRepos() {
    try {
      setRepos(await githubApi.listRepos());
    } catch {
      setError('Failed to refresh the repository list');
    }
  }

  async function handleSave() {
    if (!rows) return;
    setError(null);
    setSuccess(null);

    const capabilities = {} as Partial<Capabilities>;
    for (const key of CAPABILITY_KEYS) {
      const row = rows[key];
      if (!row.active) {
        capabilities[key] = { status: 'disabled' };
      } else if (key === 'components') {
        capabilities[key] = row.repos.length
          ? { status: 'enabled', repos: row.repos }
          : { status: 'unset' };
      } else {
        capabilities[key] = row.repo
          ? { status: 'enabled', repo: row.repo }
          : { status: 'unset' };
      }
    }

    // Plan gate, intercepted up front — the same rule the backend enforces
    // (SYD-B001): the dedicated single-repo roles cannot name more DISTINCT
    // repos than the plan allows connected. Components is exempt: it points
    // into the connected set by definition.
    const dedicated = new Set(
      CAPABILITY_KEYS.filter((key) => key !== 'components')
        .map((key) => capabilities[key])
        .filter((cap) => cap?.status === 'enabled')
        .map((cap) => cap!.repo as string),
    );
    const limit = planUsage?.repo_limit ?? null;
    if (limit !== null && dedicated.size > limit) {
      setError(
        `The ${planUsage?.plan} plan is limited to ${limit} connected ${limit === 1 ? 'repository' : 'repositories'} — ` +
        'point the roles at the same repo, or book a demo to lift the limit.',
      );
      return;
    }

    setSaving(true);
    try {
      await specsApi.saveCapabilities(capabilities);

      // Keep the CI publish target in lockstep with the fitness role.
      const fitness = capabilities.fitness;
      const fitnessRepo = fitness?.status === 'enabled' ? fitness.repo ?? '' : '';
      if (fitnessRepo && fitnessRepo !== ciRepo) {
        const [owner, repo] = fitnessRepo.split('/');
        const installation = installations.find((i) => i.account_login === owner);
        if (installation) {
          try {
            await guardrailsApi.saveCiSettings({
              owner, repo, installationId: installation.installation_id,
            });
            setCiRepo(fitnessRepo);
          } catch {
            setSuccess(null);
            setError('Roles saved, but the CI publish target could not be updated — see the CI section below.');
            return;
          }
        }
      }

      setSuccess('Repository roles saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { message?: string; detail?: string } } })?.response?.data;
      setError(data?.detail || data?.message || 'Failed to save (admin role required)');
    } finally {
      setSaving(false);
    }
  }

  if (loading || !rows) {
    return <LoadingState size="lg" />;
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <FolderGit2 className="w-5 h-5 text-slate-500" />
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Repository roles
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Which repository plays which documentation role.
          </p>
        </div>
      </div>

      <div className="mb-6 p-4 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 text-sm text-slate-600 dark:text-slate-400 space-y-2">
        <p>
          SystemDox documents five concerns. Assign each to a repository —{' '}
          <strong>one repository can play every role</strong> — or disable the concerns your
          team doesn’t use, and SystemDox stops asking about them.
        </p>
        <p>
          Every role writes and reads the paths fixed by the{' '}
          <a
            href="https://www.systemdox.com/guides/documentation-standard"
            target="_blank"
            rel="noreferrer"
            className="font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            SystemDox Documentation Standard
          </a>
          {' '}— the same folder shapes in every repository topology, so people and AI agents
          always know where requirements, decisions, and in-flight changes live.
        </p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-2 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 px-4 py-2 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 text-sm flex items-center gap-2">
          <CheckCircle className="w-4 h-4 shrink-0" />
          {success}
        </div>
      )}

      <div className="space-y-6">
        {ROWS.map(({ key, icon: Icon, title, path, description, suggestedName }) => {
          const row = rows[key];
          return (
            <div
              key={key}
              className={cn(
                'rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5',
                !row.active && 'opacity-70',
              )}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4 text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
                <code className="hidden sm:inline rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                  {path}
                </code>
                {!row.active && (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    Disabled
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setRow(key, { active: !row.active })}
                  className="ml-auto text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
                >
                  {row.active ? 'Disable — we don’t use this' : 'Enable'}
                </button>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">{description}</p>

              {row.active && key !== 'components' && (
                <>
                  <select
                    value={row.repo}
                    onChange={(e) => setRow(key, { repo: e.target.value })}
                    className="w-full max-w-md px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
                  >
                    <option value="">— not configured —</option>
                    {repos.map((r) => (
                      <option key={r.full_name} value={r.full_name}>{r.full_name}</option>
                    ))}
                  </select>
                  <CreateRepoInline
                    installations={installations}
                    suggestedName={suggestedName ?? key}
                    onCreated={(repo) => handleRepoCreated(key, repo)}
                    onRefresh={refreshRepos}
                  />
                  {declaredHosts[key]?.length ? (
                    declaredHosts[key].includes(row.repo) ? (
                      <p className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">
                        Matches the role declared in{' '}
                        <code className="px-1 rounded bg-slate-100 dark:bg-slate-800">{row.repo}</code>
                        ’s .github/docs-standard.yml
                      </p>
                    ) : (
                      <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                        {declaredHosts[key].join(', ')} declares this role in its
                        .github/docs-standard.yml — the selection above differs from the
                        committed record
                      </p>
                    )
                  ) : null}
                </>
              )}

              {row.active && key === 'components' && (
                connectedRepos.length === 0 ? (
                  <p className="text-sm text-slate-400 dark:text-slate-500">
                    Connect repositories on the GitHub page to pick component repos.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-x-5 gap-y-2">
                    {connectedRepos.map((r) => (
                      <label key={r.full_name} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                        <input
                          type="checkbox"
                          checked={row.repos.includes(r.full_name)}
                          onChange={(e) =>
                            setRow(key, {
                              repos: e.target.checked
                                ? [...row.repos, r.full_name]
                                : row.repos.filter((name) => name !== r.full_name),
                            })
                          }
                          className="rounded"
                        />
                        {r.full_name}
                      </label>
                    ))}
                  </div>
                )
              )}

              {!row.active && (
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  SystemDox won’t ask about {title.toLowerCase()} anywhere — re-enable any time.
                </p>
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="mt-6 flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
      >
        {saving && <Spinner size="sm" tone="current" />}
        Save
      </button>
    </div>
  );
}
